import { TrafficSource, type MetricDay } from "@photography-booking/shared";
import type { InsightsDay } from "../api/admin-client.js";
import {
  VIEWBOX_HEIGHT,
  VIEWBOX_WIDTH,
  areaPath,
  seriesMax,
} from "./area-path.js";

/**
 * The per-day trend chart used by every time series on the Insights tab.
 *
 * One `<svg>` of overlaid filled areas over the payload's `days[]` (Req 13.12),
 * paired with the same numbers as a real `<table>` inside a native `<details>`
 * (Req 13.10, 13.13). The chart is decorative: the `<svg>` and its legend are
 * `aria-hidden`, and every value a reader needs exists as table text whether the
 * disclosure is visually open or collapsed.
 *
 * The component is deliberately metric-agnostic — it takes a list of named
 * series plus the `Metric_Days` they run over — so the visits, traffic-source
 * and funnel sections are three uses of one chart rather than three charts.
 * Geometry comes from `area-path.ts` and every colour from the
 * `.insights-chart__series--*` rules in `packages/frontend/src/styles/theme.css`,
 * so there is no charting dependency and no hard-coded colour here (Req 13.9).
 * The `<svg>` carries a `viewBox` and no `width`/`height` attributes, so it
 * scales with its container and stays legible at 360 CSS px (Req 13.11).
 */

/** How many series colours `theme.css` provides. Series beyond this reuse them. */
const SERIES_COLOR_COUNT = 6;

/** Default `<summary>` wording for the value disclosure. */
const DEFAULT_SUMMARY_LABEL = "Show the numbers behind this chart";

/** Default header for the date column of the value table. */
const DEFAULT_DAY_COLUMN_LABEL = "Day";

/** One plotted series: an announceable name plus one value per Metric_Day. */
export interface InsightsChartSeries {
  /**
   * Everyday name for the series. Used as the value table's column header and
   * as the legend label, so it must read as text on its own.
   */
  label: string;
  /**
   * One value per entry of `days`, in the same order. A shorter array plots and
   * tabulates only the days it covers.
   */
  values: readonly number[];
}

export interface InsightsChartProps {
  /**
   * The `Metric_Days` of the selected range in ascending order: one x position
   * on the chart and one row of the value table each (Req 13.12).
   */
  days: readonly MetricDay[];
  /** The series to overlay on shared axes, in draw and column order. */
  series: readonly InsightsChartSeries[];
  /** The value table's `<caption>`, saying what the numbers are. */
  caption: string;
  /** `<summary>` wording. Defaults to "Show the numbers behind this chart". */
  summaryLabel?: string;
  /** Header for the value table's date column. Defaults to "Day". */
  dayColumnLabel?: string;
  /**
   * Formats a `Metric_Day` for its table row header. Defaults to the raw
   * `YYYY-MM-DD`, so a caller with its own date wording passes that in rather
   * than this component inventing a second format.
   */
  formatDay?: (day: MetricDay) => string;
}

/** The `theme.css` colour class for the series at `index`, cycling if needed. */
function seriesColorClass(index: number): string {
  return `insights-chart__series--${(index % SERIES_COLOR_COUNT) + 1}`;
}

/** Reads a series value for one day, treating a missing entry as 0. */
function valueAt(series: InsightsChartSeries, index: number): number {
  return series.values[index] ?? 0;
}

export function InsightsChart({
  days,
  series,
  caption,
  summaryLabel = DEFAULT_SUMMARY_LABEL,
  dayColumnLabel = DEFAULT_DAY_COLUMN_LABEL,
  formatDay = (day) => day,
}: InsightsChartProps): JSX.Element {
  // One y axis for the whole chart: the max is taken over *every* series, so
  // overlaid areas are comparable to each other rather than each filling the
  // viewBox to its own peak (Req 13.12).
  const max = seriesMax(series.map((entry) => entry.values));

  return (
    <div className="insights-chart">
      {/* Decorative: the paired table below carries the same values as text,
          so nothing here needs to be announced (Req 13.10). */}
      <svg
        className="insights-chart__svg"
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        aria-hidden="true"
        focusable="false"
      >
        {series.map((entry, index) => {
          const d = areaPath(entry.values, { max });
          if (d === "") return null;
          return (
            <path
              key={entry.label}
              className={`insights-chart__area ${seriesColorClass(index)}`}
              d={d}
            />
          );
        })}
        {/* Baseline hairline. Drawn as a <line> rather than a <path> so the
            number of <path> elements stays exactly the number of series. */}
        <line
          className="insights-chart__axis"
          x1={0}
          y1={VIEWBOX_HEIGHT}
          x2={VIEWBOX_WIDTH}
          y2={VIEWBOX_HEIGHT}
        />
      </svg>

      <ul className="insights-chart__legend" aria-hidden="true">
        {series.map((entry, index) => (
          <li className="insights-chart__legend-item" key={entry.label}>
            <span
              className={`insights-chart__legend-swatch ${seriesColorClass(
                index,
              )}`}
            />
            {entry.label}
          </li>
        ))}
      </ul>

      {/* A native <details> with no `open` attribute: it renders collapsed the
          first time a range's metrics are shown and again after every range
          change (Req 13.13), while its table stays in the document and in the
          accessibility tree throughout (Req 13.10). */}
      <details className="insights-details">
        <summary className="insights-details__summary">{summaryLabel}</summary>
        <div className="insights-details__body">
          <div className="insights-table-wrap">
            <table className="insights-table">
              <caption>{caption}</caption>
              <thead>
                <tr>
                  <th scope="col">{dayColumnLabel}</th>
                  {series.map((entry) => (
                    <th
                      scope="col"
                      className="insights-table__number"
                      key={entry.label}
                    >
                      {entry.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {days.map((day, index) => (
                  <tr key={day}>
                    <th scope="row">{formatDay(day)}</th>
                    {series.map((entry) => (
                      <td className="insights-table__number" key={entry.label}>
                        {valueAt(entry, index)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </details>
    </div>
  );
}

// ---- "Where visitors came from" series selection ------------------------

/**
 * How many named `Traffic_Sources` are plotted individually.
 *
 * The payload carries all eight sources for every day (Req 12.8) and the cap is
 * applied here rather than in the API, because eight overlaid areas are
 * unreadable at 360 CSS px. Five named series plus the aggregate below makes six
 * — the number of colours `theme.css` provides.
 */
export const TRAFFIC_SOURCE_SERIES_LIMIT = 5;

/** Label of the series that carries every source outside the top five. */
export const OTHER_SOURCES_LABEL = "Other sources";

/**
 * Every `Traffic_Source` in a fixed order, used only to break ties in the
 * selection below so the chosen set is a function of the payload rather than of
 * object key order.
 *
 * Re-declared here because the backend's `TRAFFIC_SOURCE_ORDER` lives in
 * `packages/backend` and the frontend does not — and must not — depend on it.
 * The order matches the backend's so a tie is broken the same way on both sides.
 */
export const TRAFFIC_SOURCE_ORDER: readonly TrafficSource[] = [
  TrafficSource.DIRECT,
  TrafficSource.INSTAGRAM,
  TrafficSource.TIKTOK,
  TrafficSource.FACEBOOK,
  TrafficSource.GOOGLE,
  TrafficSource.BING,
  TrafficSource.SAME_SITE,
  TrafficSource.OTHER,
];

/** Tie-break rank: known sources in order, anything unknown after them. */
function sourceRank(source: string): number {
  const index = TRAFFIC_SOURCE_ORDER.indexOf(source as TrafficSource);
  return index === -1 ? TRAFFIC_SOURCE_ORDER.length : index;
}

/** Every source key appearing anywhere in the range, in tie-break order. */
function sourceKeys(days: readonly InsightsDay[]): string[] {
  const keys = new Set<string>();
  for (const day of days) {
    for (const key of Object.keys(day.trafficSources)) {
      keys.add(key);
    }
  }
  return [...keys].sort((a, b) => {
    const byRank = sourceRank(a) - sourceRank(b);
    return byRank !== 0 ? byRank : a.localeCompare(b);
  });
}

/**
 * Chooses the series for "Where visitors came from".
 *
 * Takes the {@link TRAFFIC_SOURCE_SERIES_LIMIT} sources with the largest totals
 * over the whole range, largest first, ties broken by
 * {@link TRAFFIC_SOURCE_ORDER}, then sums **every** remaining source per day
 * into one {@link OTHER_SOURCES_LABEL} series. Nothing is dropped, so the
 * plotted series still sum to each day's site-view total (Req 12.8) and the
 * disclosure table lists the same series as the chart.
 *
 * The aggregate series is appended only when there is something left to
 * aggregate, so a payload carrying five or fewer sources plots exactly those.
 */
export function trafficSourceSeries(
  days: readonly InsightsDay[],
): InsightsChartSeries[] {
  const keys = sourceKeys(days);
  const totalFor = (key: string): number =>
    days.reduce((sum, day) => sum + (day.trafficSources[key as TrafficSource] ?? 0), 0);

  const ranked = keys
    .map((key) => ({ key, total: totalFor(key) }))
    // `keys` is already in tie-break order and `sort` is stable, so equal
    // totals keep that order without comparing ranks again.
    .sort((a, b) => b.total - a.total);

  const chosen = ranked.slice(0, TRAFFIC_SOURCE_SERIES_LIMIT);
  const rest = ranked.slice(TRAFFIC_SOURCE_SERIES_LIMIT);

  const series: InsightsChartSeries[] = chosen.map((entry) => ({
    label: entry.key,
    values: days.map((day) => day.trafficSources[entry.key as TrafficSource] ?? 0),
  }));

  if (rest.length > 0) {
    series.push({
      label: OTHER_SOURCES_LABEL,
      values: days.map((day) =>
        rest.reduce(
          (sum, entry) =>
            sum + (day.trafficSources[entry.key as TrafficSource] ?? 0),
          0,
        ),
      ),
    });
  }

  return series;
}
